import { useConversations } from '../../hooks/useConversations';

export default function ChatSidebar({ 
  showSidebar, 
  onCloseSidebar, 
  onNewChat,
  onSelectConversation 
}) {
  const { conversations, loading, error, refreshConversations } = useConversations();

  if (!showSidebar) return null;

  /**
   * Format timestamp to human-readable "time ago"
   */
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    return then.toLocaleDateString();
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onCloseSidebar}
      />
      
      {/* Sidebar Panel */}
      <div className="fixed top-0 left-0 w-80 h-full bg-gray-900 text-white z-50 flex flex-col">
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <button
            onClick={onCloseSidebar}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <h2 className="text-lg font-semibold">AI Inspect</h2>
          <button
            onClick={refreshConversations}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            title="Refresh conversations"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
            </svg>
          </button>
        </div>
        
        {/* New Chat Button */}
        <div className="p-4">
          <button
            onClick={() => {
              onNewChat();
              onCloseSidebar();
            }}
            className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-800 rounded-lg transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            New chat
          </button>
        </div>
        
        {/* Chat History */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {loading && (
            <div className="text-center text-gray-400 py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
              <div className="text-sm">Loading conversations...</div>
            </div>
          )}
          
          {error && (
            <div className="text-center text-red-400 py-8">
              <div className="text-sm">Failed to load conversations</div>
              <button 
                onClick={refreshConversations}
                className="text-xs text-gray-400 hover:text-white mt-2"
              >
                Try again
              </button>
            </div>
          )}
          
          {!loading && !error && conversations.length === 0 && (
            <div className="text-center text-gray-400 py-8">
              <svg className="w-12 h-12 mx-auto mb-2 opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <div className="text-sm">No conversations yet</div>
              <div className="text-xs mt-1">Start a new chat to begin</div>
            </div>
          )}
          
          {!loading && !error && conversations.length > 0 && (
            <div className="space-y-2">
              {conversations.map((conv) => (
                <div
                  key={conv.id}
                  className="p-3 hover:bg-gray-800 rounded-lg cursor-pointer transition-colors group"
                  onClick={() => {
                    // ✅ FIX: Use conversationId if available, otherwise fall back to sessionId
                    const idToUse = conv.conversationId || conv.sessionId;
                    onSelectConversation(idToUse, conv.conversationId ? 'conversationId' : 'sessionId');
                    onCloseSidebar();
                  }}
                >
                  <div className="text-sm font-medium line-clamp-2">{conv.title}</div>
                  <div className="text-xs text-gray-400 mt-1 flex items-center justify-between">
                    <span>{formatTimeAgo(conv.lastMessageAt)}</span>
                    {conv.isActive && (
                      <span className="flex items-center gap-1 text-green-400">
                        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                        Active
                      </span>
                    )}
                  </div>
                  {conv.messageCount > 0 && (
                    <div className="text-xs text-gray-500 mt-1">
                      {conv.messageCount} message{conv.messageCount > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
